import { DEFAULT_QUERY_OPTIONS } from "#hooks/adapters/adapterUtils";
import { useParams } from "#routers/hooks";
import { type DataSetSubmissionDetailRoutePath } from "#routes/data-tools/submissions/datasets/[transactionId]/detail";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@validereinc/common-components";
import {
  GetListRequestType,
  Resources,
  TransactionAdapter,
  TransactionGetListFilterType,
  TransactionStatus,
} from "@validereinc/domain";
import { downloadFile } from "@validereinc/utilities";

export const useListTransactions = (
  apiParams: GetListRequestType<TransactionGetListFilterType>
) =>
  useQuery({
    queryKey: ["transaction", "list", apiParams],
    queryFn: () => TransactionAdapter.getList(apiParams),
    staleTime: 3 * 60 * 1000,
  });

export const useGetOneTransaction = (
  params: Parameters<typeof TransactionAdapter.getOne>[0],
  opts: { enabled?: boolean } = {}
) => {
  return useQuery({
    queryKey: [Resources.TRANSACTION, params],
    queryFn: () => TransactionAdapter.getOne(params),
    enabled: !!params.id,
    ...opts,
    ...DEFAULT_QUERY_OPTIONS,
    retry: false,
    refetchOnWindowFocus: true,
    refetchInterval: (data) => {
      const lastTx = data?.data[data.data.length - 1];

      return lastTx?.status === TransactionStatus.PENDING ||
        lastTx?.status === TransactionStatus.FAILED
        ? lastTx?.status === TransactionStatus.FAILED
          ? 15000
          : 5000
        : false;
    },
  });
};

export const useGetOneTransactionDetail = () => {
  const { transactionId } =
    useParams<
      Exclude<typeof DataSetSubmissionDetailRoutePath.pathParams, undefined>
    >();

  const params: Parameters<typeof TransactionAdapter.details.getOne>[0] = {
    id: transactionId ?? "",
  };

  return useQuery({
    queryKey: [Resources.TRANSACTION, "details", params],
    queryFn: () => TransactionAdapter.details.getOne(params),
    enabled: !!params.id,
    ...DEFAULT_QUERY_OPTIONS,
    refetchOnWindowFocus: true,
  });
};

export const useDownloadTransactionRowLevelDetails = () => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      id,
      meta,
      fileName,
    }: Parameters<typeof TransactionAdapter.details.getOne>[0] & {
      fileName: string;
    }) => {
      const data = await TransactionAdapter.details.getOne({
        id,
        meta: { accept: "text/csv", ...(meta ?? {}) },
      });
      downloadFile(fileName, data);
    },
    onSuccess: () => {
      toast.push({
        intent: "success",
        description:
          "Successfully downloaded dataset submission row-level details as CSV",
      });
    },
    onError: (err) => {
      toast.push({
        intent: "error",
        description:
          "Failed to download dataset submission row-level details as CSV",
      });
      console.error(err);
    },
  });
};

export const useOriginalSubmissionFileDownload = () => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      transactionId,
      fileName,
      onlyErrors = false,
      hasTransformed = false,
    }: Parameters<typeof TransactionAdapter.download.getOne>[0] & {
      fileName: string;
    }) => {
      const data = await TransactionAdapter.download.getOne({
        transactionId,
        hasTransformed,
        onlyErrors,
      });

      // download the blob as a file
      downloadFile(fileName, data);
    },
    onSuccess: () => {
      toast.push({
        intent: "success",
        description: "Successfully downloaded original submission",
      });
    },
    onError: (err) => {
      toast.push({
        intent: "error",
        description: "Could not download the original submission",
      });
      console.error(err);
    },
  });
};

export const useInvalidateTransaction = () => {
  const queryClient = useQueryClient();

  return {
    invalidate: () =>
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === Resources.TRANSACTION,
      }),
  };
};
